//
// WARNING
//
// this mixin can be only used on root component
// this file is separated just for code splitting / readability purposes
//

import Vue from 'vue'
import NProgress from 'nprogress'
import queryString from 'query-string'
import pickBy from 'lodash/pickBy'
import isEqual from 'lodash/isEqual'
import Multiselect from 'vue-multiselect'
import metricSuffix from 'metric-suffix'
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock'
import * as ranges from '../ranges'

import 'vue-multiselect/dist/vue-multiselect.min.css'

import LocationsPicker from '../locations-picker.vue'
import carouselObserver from '../../carousel-observer'

const paginationParams = ['page', 'direction', 'sort']
const arrayFormat = 'bracket'
const defaultFilters = {
  location: null,
  type: null,
  priceMin: null,
  priceMax: null,
  priceSqmMin: null,
  priceSqmMax: null,
  surfaceMin: null,
  surfaceMax: null,
  isNewProject: false,
  has360: false,
  sort: null,
  page: null,
  direction: null,
  priceSqm: false,
  bounds: null,
}

const addApostrophesToNumber = number => {
  let result = ''
  String(number)
    .split('')
    .reverse()
    .forEach((char, i) => {
      if (i % 3 === 0 && i !== 0) {
        result = `'${result}`
      }
      result = `${char}${result}`
    })
  return result
}

export default {
  components: { LocationsPicker, Multiselect },
  data() {
    const topTitle = document.getElementById('topTitle')
    const bottomTitle = document.getElementById('bottomTitle')
    const bottomDescription = document.getElementById('bottomDescription')
    const filters = this.getStateSyncedWithUrl(window.location.search)
    return {
      filters,
      mapResults: [],
      allResultsAreLoading: false,
      paginatedResultsAreLoading: false,
      showMobileFilters: false,
      seo: {
        topTitle: topTitle ? topTitle.textContent : '',
        bottomTitle: bottomTitle ? bottomTitle.textContent : '',
        bottomDescription: bottomDescription ? bottomDescription.innerHTML : '',
      },
      loaded: false,
      noRefresh: false,
      reloadOnChange: false,
      popStateListener: false,
      windowWidth: window.innerWidth,
      rent: null,
    }
  },
  computed: {
    nonEmptyFilters() {
      return pickBy(this.filters, Boolean)
    },
    priceSuggestions() {
      if (!this.rent) {
        return ranges.buyBudget
      }
      if (this.filters.priceSqm) {
        return ranges.rentPerSqm
      }
      return ranges.rentPerMonth
    },
    minPriceSuggestions() {
      const limit = this.filters.priceSqm
        ? this.filters.priceSqmMax
        : this.filters.priceMax
      return this.priceSuggestions.filter(el => el < limit || Infinity)
    },
    maxPriceSuggestions() {
      const limit = this.filters.priceSqm
        ? this.filters.priceSqmMin
        : this.filters.priceMin
      return this.priceSuggestions.filter(el => el > limit || 0)
    },
    minSurfaceSuggestions() {
      return ranges.surface.filter(
        el => el < this.filters.surfaceMax || Infinity
      )
    },
    maxSurfaceSuggestions() {
      return ranges.surface.filter(el => el > this.filters.surfaceMin || 0)
    },
    displayNoResults() {
      return this.loaded && this.mapResults.length === 0
    },
    updatesAreDisabled() {
      return this.showMobileFilters || this.noRefresh || !this.reloadOnChange
    },
    surfaceSelectionDisplay() {
      const { filters } = this
      if (filters.surfaceMin && filters.surfaceMax) {
        return `${this.formatSurfaceOption(
          filters.surfaceMin
        )} - ${this.formatSurfaceOption(filters.surfaceMax)}`
      }
      if (!filters.surfaceMin && filters.surfaceMax) {
        return `< ${this.formatSurfaceOption(filters.surfaceMax)}`
      }
      if (filters.surfaceMin && !filters.surfaceMax) {
        return `> ${this.formatSurfaceOption(filters.surfaceMin)}`
      }
      return ''
    },
    priceSelectionDisplay() {
      const { filters } = this
      const min = this.filters.priceSqm ? filters.priceSqmMin : filters.priceMin
      const max = this.filters.priceSqm ? filters.priceSqmMax : filters.priceMax
      if (min && max) {
        return `${this.formatPriceOption(min, true)} - ${metricSuffix(max, 3)}`
      }
      if (!min && max) {
        return `< ${this.formatPriceOption(max, true)}`
      }
      if (min && !max) {
        return `> ${this.formatPriceOption(min, true)}`
      }
      return ''
    },
    stringifyNonEmpty() {
      return queryString.stringify(this.nonEmptyFilters, {
        arrayFormat,
      })
    },
    boundsObject() {
      return this.filters.bounds ? JSON.parse(this.filters.bounds) : null
    },
    topbarOptionsLimit() {
      if (this.windowWidth > 2500) {
        return 3
      }
      if (this.windowWidth > 1900) {
        return 2
      }
      return 1
    },
    currentRoute() {
      return window.paths[this.rent ? 'rent' : 'buy']
    },
    currentRouteCards() {
      return `${window.paths.searchCardsPaginated}/${Number(this.rent)}`
    },
  },
  methods: {
    getStateSyncedWithUrl(url) {
      const urlState = queryString.parse(url, {
        arrayFormat,
        parseBooleans: true,
        parseNumbers: true,
      })
      return { ...defaultFilters, ...urlState }
    },
    handleServerRenderedLinkClick(e) {
      e.preventDefault()
      const searchString = e.target
        .getAttribute('href')
        .split('?')
        .pop()

      // save current sort

      const newSearch = queryString.parse(searchString, {
        arrayFormat,
        parseBooleans: true,
        parseNumbers: true,
      })
      paginationParams.forEach(key => {
        Vue.set(this.filters, key, newSearch[key])
      })
      NProgress.start()
      this.paginatedResultsAreLoading = true

      const isLoadMore = 'loadMore' in e.target.dataset
      const promise = isLoadMore
        ? this.loadMore(searchString)
        : this.updatePaginatedCards(searchString)

      promise.then(() => {
        NProgress.done()
        this.paginatedResultsAreLoading = false
      })
    },
    initServerRenderedHtml(element) {
      element.querySelectorAll('.pagination a, .sort a').forEach(a => {
        a.addEventListener('click', this.handleServerRenderedLinkClick)
      })

      element.querySelectorAll('[data-open-detail]').forEach(a => {
        a.addEventListener('click', e => {
          e.preventDefault()
          this.openDetail(a.dataset.openDetail)
        })
      })

      element
        .querySelectorAll('[data-carousel]:not(.swiper-container-initialized)')
        .forEach(el => {
          carouselObserver.observe(el)
          el.addEventListener('click', e => {
            e.preventDefault()
            e.stopPropagation()
          })
        })
    },
    updatePaginatedCards(searchString, options = {}) {
      const { updateUrl = true } = options
      window.scrollTo(0, 0)
      return fetch(`${this.currentRouteCards}?${searchString}`)
        .then(res => res.text())
        .then(html => {
          this.$refs.cards.innerHTML = html
          this.initServerRenderedHtml(this.$refs.cards)

          if (updateUrl) {
            const route = window.paths[this.rent ? 'rent' : 'buy']
            window.history.pushState(null, false, `${route}?${searchString}`)
          }
        })
        .catch(e => {
          console.error(e) // don't remove this!
          // this.$refs.cards.innerHTML = `<p class="error">${JSON.stringify(e)}</p>`
        })
    },
    loadMore(searchString) {
      return fetch(`${this.currentRouteCards}?${searchString}`)
        .then(res => res.text())
        .then(html => {
          const responseWrapper = document.createElement('div')
          responseWrapper.innerHTML = html

          const listOfLists = this.$refs.cards.querySelector(
            '[data-list-of-lists]'
          )
          const newList = responseWrapper.querySelector('[data-list]')
          listOfLists.appendChild(newList)

          const existingPagination = this.$refs.cards.querySelector(
            '[data-pagination]'
          )
          const newPagination = responseWrapper.querySelector(
            '[data-pagination]'
          )
          existingPagination.innerHTML = newPagination.innerHTML

          this.initServerRenderedHtml(newList)
          this.initServerRenderedHtml(existingPagination)
        })
        .catch(e => {
          console.error(e) // don't remove this!
          // this.$refs.cards.innerHTML = `<p class="error">${JSON.stringify(e)}</p>`
        })
    },
    updateMarkersOnMap(options = {}) {
      const { updateResults = true, updateSeo = false } = options
      return fetch(
        `${window.paths.searchJson}/${Number(this.rent)}?${
          this.stringifyNonEmpty
        }`
      )
        .then(res => res.json())
        .then(json => {
          if (updateResults) {
            this.mapResults = json.results
          }
          if (updateSeo) {
            this.seo = json.seo
          }
        })
    },
    handleFiltersChange(updateUrl = true) {
      if (this.updatesAreDisabled) return
      if (updateUrl) {
        this.filters.page = null
      }

      NProgress.start()
      this.allResultsAreLoading = true

      Promise.all([
        this.updateMarkersOnMap({ updateSeo: true }),
        this.updatePaginatedCards(this.stringifyNonEmpty, { updateUrl }),
      ]).then(() => {
        NProgress.done()
        this.allResultsAreLoading = false
      })
    },
    formatPriceOption(number, useMetricSuffix) {
      const formattedNumber = useMetricSuffix
        ? metricSuffix(number, 3)
        : addApostrophesToNumber(number)
      return `CHF ${formattedNumber}`
    },
    formatSurfaceOption(number) {
      return `${addApostrophesToNumber(number)} m²`
    },
    clearAll() {
      const { rent } = this.filters
      this.filters = { ...defaultFilters, rent }
    },
    togglePricePerSqm(yes) {
      if (yes) {
        this.filters.priceMin = null
        this.filters.priceMax = null
      } else {
        this.filters.priceSqmMin = null
        this.filters.priceSqmMax = null
      }
      this.filters.priceSqm = yes
      this.handleFiltersChange()
    },
    syncFiltersToUrl() {
      const newFilters = this.getStateSyncedWithUrl(window.location.search)
      if (!isEqual(this.filters, newFilters)) {
        this.filters = newFilters
        this.noRefresh = true
        setTimeout(() => {
          this.noRefresh = false
          this.handleFiltersChange(false)
        }, 0)
      }
    },
    navigateAwayToOffers() {
      window.location = `${this.currentRoute}?${this.stringifyNonEmpty}`
    },
    onBoundsChange(bounds) {
      this.filters.bounds = JSON.stringify(bounds)
      this.noRefresh = true
      this.filters.location = null
      setTimeout(() => {
        this.noRefresh = false
        this.handleFiltersChange()
      }, 0)
    },
    handleResize() {
      this.windowWidth = window.innerWidth
    },
  },

  watch: {
    rent(newValue, oldValue) {
      if (oldValue === null) return
      if (!this.noRefresh) {
        this.filters.priceMin = null
        this.filters.priceMax = null
        this.filters.priceSqmMin = null
        this.filters.priceSqmMax = null
        this.filters.type = null
      }
      if (!this.rent) {
        this.filters.priceSqm = false
      }
      this.handleFiltersChange()
    },
    'filters.location': function() {
      if (!this.noRefresh) {
        this.filters.bounds = null
      }
      this.handleFiltersChange()
    },
    'filters.type': function() {
      this.handleFiltersChange()
    },
    'filters.has360': function() {
      this.handleFiltersChange()
    },
    'filters.isNewProject': function() {
      this.handleFiltersChange()
    },
    showMobileFilters() {
      // wait until ref is accessible
      setTimeout(() => {
        if (this.showMobileFilters) {
          disableBodyScroll(this.$refs.mobileFilters)
        } else {
          clearAllBodyScrollLocks()
          this.handleFiltersChange()
        }
      }, 0)
    },
  },

  mounted() {
    this.reloadOnChange = 'reloadOnChange' in this.$root.$el.dataset
    this.rent = 'rent' in this.$root.$el.dataset

    // if on offers page
    if (this.reloadOnChange) {
      this.updateMarkersOnMap().then(() => (this.loaded = true))
      this.initServerRenderedHtml(this.$refs.cards)

      window.addEventListener('popstate', this.syncFiltersToUrl)
      this.popStateListener = true
    }

    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    if (this.popStateListener) {
      window.removeEventListener('popstate', this.syncFiltersToUrl)
    }
    window.removeEventListener('resize', this.handleResize)
  },
}
